/**
 * PersonaForm Component
 *
 * Reusable form for creating and editing AI personas.
 * Includes CodeMirror-based prompt editor with markdown syntax highlighting.
 *
 * Feature: 071-ai-critique-sessions (US2, T047-T048)
 */

import { useState, useEffect } from 'react';
import CodeMirror from '@uiw/react-codemirror';
import { markdown } from '@codemirror/lang-markdown';
import { useTheme } from '@/theme';
import {
  AIPersona,
  CreateAIPersonaInput,
  UpdateAIPersonaInput,
  AI_PROVIDERS,
  AI_MODELS,
  getProviderDisplayName,
} from '@/hooks/useAIPersonas';

interface PersonaFormProps {
  /** Existing persona for edit mode (undefined for create mode) */
  persona?: AIPersona;
  /** Called on form submission with validated data */
  onSubmit: (data: CreateAIPersonaInput | UpdateAIPersonaInput) => Promise<void>;
  /** Called when user cancels */
  onCancel: () => void;
  /** Whether the form is submitting */
  isSubmitting?: boolean;
  /** Submit button label */
  submitLabel?: string;
}

/**
 * Validate username format
 */
function isValidUsername(username: string): boolean {
  return /^[a-z0-9-]+$/.test(username);
}

export function PersonaForm({
  persona,
  onSubmit,
  onCancel,
  isSubmitting = false,
  submitLabel = 'Save',
}: PersonaFormProps) {
  const { effectiveTheme } = useTheme();
  const isEditMode = !!persona;

  // Form state
  const [username, setUsername] = useState(persona?.username || '');
  const [displayName, setDisplayName] = useState(persona?.displayName || '');
  const [provider, setProvider] = useState<'openai' | 'anthropic' | 'google'>(
    persona?.aiProvider || 'anthropic'
  );
  const [model, setModel] = useState(persona?.aiModel || AI_MODELS.anthropic[0]);
  const [prompt, setPrompt] = useState(persona?.aiPrompt || '');

  // Validation errors
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Update model when provider changes (only if current model is not valid for new provider)
  useEffect(() => {
    if (!AI_MODELS[provider].includes(model)) {
      setModel(AI_MODELS[provider][0]);
    }
  }, [provider, model]);

  // Available models for current provider
  const availableModels = AI_MODELS[provider];

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!username.trim()) {
      newErrors.username = 'Username is required';
    } else if (!isValidUsername(username)) {
      newErrors.username = 'Username must be lowercase alphanumeric with hyphens only';
    }

    if (!displayName.trim()) {
      newErrors.displayName = 'Display name is required';
    }

    if (!prompt.trim()) {
      newErrors.prompt = 'Prompt is required';
    } else if (prompt.length < 10) {
      newErrors.prompt = 'Prompt must be at least 10 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (isEditMode) {
      // For edit mode, only include changed fields
      const updates: UpdateAIPersonaInput = {};
      if (username !== persona?.username) updates.username = username;
      if (displayName !== persona?.displayName) updates.displayName = displayName;
      if (provider !== persona?.aiProvider) updates.provider = provider;
      if (model !== persona?.aiModel) updates.model = model;
      if (prompt !== persona?.aiPrompt) updates.prompt = prompt;

      await onSubmit(updates);
    } else {
      // For create mode, include all fields
      await onSubmit({
        username,
        displayName,
        provider,
        model,
        prompt,
      });
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Username */}
      <div>
        <label
          htmlFor="username"
          className="block text-sm font-medium text-foreground mb-1"
        >
          Username
        </label>
        <input
          type="text"
          id="username"
          value={username}
          onChange={(e) => setUsername(e.target.value.toLowerCase())}
          placeholder="claude-critic"
          className={`w-full px-3 py-2 border rounded-md bg-surface-1 text-foreground focus:outline-none focus:ring-2 focus:ring-primary ${
            errors.username ? 'border-red-500' : 'border-border'
          }`}
          disabled={isSubmitting}
        />
        {errors.username && (
          <p className="mt-1 text-sm text-red-500">{errors.username}</p>
        )}
        <p className="mt-1 text-xs text-muted">
          Lowercase letters, numbers, and hyphens only. Used for @-mentions.
        </p>
      </div>

      {/* Display Name */}
      <div>
        <label
          htmlFor="displayName"
          className="block text-sm font-medium text-foreground mb-1"
        >
          Display Name
        </label>
        <input
          type="text"
          id="displayName"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          placeholder="Claude Critic"
          className={`w-full px-3 py-2 border rounded-md bg-surface-1 text-foreground focus:outline-none focus:ring-2 focus:ring-primary ${
            errors.displayName ? 'border-red-500' : 'border-border'
          }`}
          disabled={isSubmitting}
        />
        {errors.displayName && (
          <p className="mt-1 text-sm text-red-500">{errors.displayName}</p>
        )}
      </div>

      {/* Provider */}
      <div>
        <label
          htmlFor="provider"
          className="block text-sm font-medium text-foreground mb-1"
        >
          AI Provider
        </label>
        <select
          id="provider"
          value={provider}
          onChange={(e) => setProvider(e.target.value as typeof provider)}
          className="w-full px-3 py-2 border border-border rounded-md bg-surface-1 text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          disabled={isSubmitting}
        >
          {AI_PROVIDERS.map((p) => (
            <option key={p} value={p}>
              {getProviderDisplayName(p)}
            </option>
          ))}
        </select>
      </div>

      {/* Model */}
      <div>
        <label
          htmlFor="model"
          className="block text-sm font-medium text-foreground mb-1"
        >
          Model
        </label>
        <select
          id="model"
          value={model}
          onChange={(e) => setModel(e.target.value)}
          className="w-full px-3 py-2 border border-border rounded-md bg-surface-1 text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          disabled={isSubmitting}
        >
          {availableModels.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>
      </div>

      {/* Prompt Editor */}
      <div>
        <label
          htmlFor="prompt"
          className="block text-sm font-medium text-foreground mb-1"
        >
          System Prompt
        </label>
        <div
          className={`border rounded-md overflow-hidden ${
            errors.prompt ? 'border-red-500' : 'border-border'
          }`}
        >
          <CodeMirror
            value={prompt}
            height="300px"
            extensions={[markdown()]}
            onChange={setPrompt}
            theme={effectiveTheme === 'dark' ? 'dark' : 'light'}
            readOnly={isSubmitting}
            placeholder="You are an expert photography critic..."
            basicSetup={{
              lineNumbers: true,
              highlightActiveLineGutter: true,
              highlightSpecialChars: true,
              foldGutter: true,
              drawSelection: true,
              dropCursor: true,
              allowMultipleSelections: true,
              indentOnInput: true,
              bracketMatching: true,
              closeBrackets: true,
              autocompletion: false,
              rectangularSelection: true,
              crosshairCursor: true,
              highlightActiveLine: true,
              highlightSelectionMatches: true,
              closeBracketsKeymap: true,
              searchKeymap: true,
              foldKeymap: true,
              completionKeymap: true,
              lintKeymap: true,
            }}
          />
        </div>
        {errors.prompt && (
          <p className="mt-1 text-sm text-red-500">{errors.prompt}</p>
        )}
        <p className="mt-1 text-xs text-muted">
          The system prompt that defines this AI persona's critique style. Supports markdown formatting.
        </p>
      </div>

      {/* Form Actions */}
      <div className="flex justify-end gap-3 pt-4 border-t border-border">
        <button
          type="button"
          onClick={onCancel}
          disabled={isSubmitting}
          className="px-4 py-2 border border-border rounded-md hover:bg-surface-2 transition-colors disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50"
        >
          {isSubmitting ? 'Saving...' : submitLabel}
        </button>
      </div>
    </form>
  );
}
